import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from 'pdfjs-dist'

// Use local worker file to avoid CDN issues
if (typeof pdfjsLib.GlobalWorkerOptions !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs'
}

export default class extends Controller {
  static targets = ["pageNum", "pageCount", "canvas"]
  static values = { url: String }

  connect() {
    console.log('PDF Viewer Controller connected')
    console.log('PDF URL:', this.urlValue)

    this.currentPage = 1
    this.pdfDoc = null
    this.pageRendering = false
    this.pageNumPending = null
    this.scale = 1.5

    if (!this.urlValue) {
      console.error('No PDF URL provided')
      this.showError(t('javascript.controllers.pdf_viewer_controller.no_pdf_url'))
      return
    }

    this.loadPDF()
  }

  async loadPDF() {
    try {
      console.log('Loading PDF from URL:', this.urlValue)

      // Configure loading options with credentials for same-origin requests
      const loadingTask = pdfjsLib.getDocument({
        url: this.urlValue,
        withCredentials: true,
        // Disable range requests to load entire PDF at once
        disableRange: false,
        // Enable streaming
        disableStream: false
      })

      this.pdfDoc = await loadingTask.promise

      console.log('PDF loaded successfully, pages:', this.pdfDoc.numPages)

      if (this.hasPageCountTarget) {
        this.pageCountTarget.textContent = this.pdfDoc.numPages
      }

      this.renderPage(this.currentPage)
    } catch (error) {
      console.error('Error loading PDF:', error)
      console.error('Error details:', error.message, error.name)
      if (error.name === 'MissingPDFException') {
        this.showError(t('javascript.controllers.pdf_viewer_controller.pdf_missing_or_corrupted'))
      } else if (error.name === 'UnexpectedResponseException') {
        this.showError(t('javascript.controllers.pdf_viewer_controller.unable_to_load_pdf'))
      } else {
        this.showError(t('javascript.controllers.pdf_viewer_controller.failed_to_load_pdf'))
      }
    }
  }

  showError(message) {
    const canvas = this.hasCanvasTarget ? this.canvasTarget : document.getElementById('pdf-canvas')
    if (canvas) {
      const parent = canvas.parentElement
      parent.innerHTML = `
        <div class="p-8 text-center">
          <span class="material-symbols-outlined text-6xl text-red-500 mb-4 block">error</span>
          <p class="text-gray-300 mb-4">${message}</p>
          <a href="${this.urlValue}" target="_blank" class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg">
            <span class="material-symbols-outlined">open_in_new</span>
            ${t('javascript.controllers.pdf_viewer_controller.open_in_new_tab')}
          </a>
        </div>
      `
    }
  }

  async renderPage(num) {
    this.pageRendering = true

    try {
      const page = await this.pdfDoc.getPage(num)
      const canvas = this.hasCanvasTarget ? this.canvasTarget : document.getElementById('pdf-canvas')
      const ctx = canvas.getContext('2d')
      const viewport = page.getViewport({ scale: this.scale })

      canvas.height = viewport.height
      canvas.width = viewport.width

      const renderContext = {
        canvasContext: ctx,
        viewport: viewport
      }

      await page.render(renderContext).promise

      this.pageRendering = false

      if (this.hasPageNumTarget) {
        this.pageNumTarget.textContent = num
      }

      if (this.pageNumPending !== null) {
        this.renderPage(this.pageNumPending)
        this.pageNumPending = null
      }
    } catch (error) {
      console.error('Error rendering page:', error)
      this.pageRendering = false
      this.showError(t('javascript.controllers.pdf_viewer_controller.failed_to_render_page'))
    }
  }

  queueRenderPage(num) {
    if (this.pageRendering) {
      this.pageNumPending = num
    } else {
      this.renderPage(num)
    }
  }

  previousPage(event) {
    event.preventDefault()
    if (this.currentPage <= 1) {
      return
    }
    this.currentPage--
    this.queueRenderPage(this.currentPage)
  }

  nextPage(event) {
    event.preventDefault()
    if (this.currentPage >= this.pdfDoc.numPages) {
      return
    }
    this.currentPage++
    this.queueRenderPage(this.currentPage)
  }
}
